import axios from 'axios';
import { isTokenExpired, decodeToken } from '../utils/dataUtils';

const API_BASE_URL = 'https://erp-server-3qrl.onrender.com/api';
// const API_BASE_URL = 'http://localhost:5000/api';

const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer your-token', // Add this if you're using authentication
        'x-session-id': 'your-session-id', // Add this if you're using custom headers
    },
    withCredentials: true,
});

// Add a request interceptor to include the JWT token in headers
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    const sessionId = localStorage.getItem('sessionId'); // Retrieve sessionId
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    if (sessionId) {
        config.headers['X-Session-ID'] = sessionId; // Add sessionId to headers
    }
    // console.log('Request Headers:', config.headers);
    return config;
});

// Handle session expiry
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response && error.response.status === 401) {
            // Redirect to login page if session expires
            logoutUser();
        }
        return Promise.reject(error);
    }
);

export const loginUser = async (email, password) => {
    try {
        const response = await api.post('/auth/login', {
            email,
            password,
        }); // Notify backend to remove the session
        localStorage.setItem('token', response.data.token); // Save token to localStorage
        const token = localStorage.getItem('token');

        if (token) {
            const userData = decodeToken(token);
            // console.log('Decoded User Data:', userData);
            localStorage.setItem('userCompany', userData.company); // Store user name separately
            localStorage.setItem('userRole', userData.role); // Store user name separately
            localStorage.setItem('userName', userData.fullname); // Store user name separately
            localStorage.setItem('sessionId', userData.sessionId); // Save token to localStorage
        }
        window.location.href = '/dashboard'; // Adjust the login route as needed
    } catch (error) {
        console.error('Login failed:', error);
    }
};

export const signupUser = async (fullname, phone, email, password, role) => {
    try {
        const response = await api.post('/auth/register', {
            fullname,
            phone,
            email,
            password,
            role,
        });
        return response.data;
    } catch (error) {
        console.error('New user failed:', error);
    }
};

export const logoutUser = async () => {
    try {
        const sessionId = localStorage.getItem('sessionId');
        await api.post('/auth/logout', { sessionId }); // Notify backend to remove the session
    } catch (error) {
        console.error('Logout failed:', error);
    } finally {
        // Clear localStorage
        localStorage.removeItem('token');
        localStorage.removeItem('userRole');
        localStorage.removeItem('userName');
        localStorage.removeItem('userCompany');
        localStorage.removeItem('sessionId');

        // Redirect to login page
        window.location.href = '/login'; // Adjust the login route as needed
    }
};

export const checkTokenAndLogout = () => {
    const token = localStorage.getItem('token');

    if (isTokenExpired(token)) {
        logoutUser(); // Auto-logout if token is expired
    }
};

export const getUserById = async (userId) => {
    try {
        const response = await api.get(`/users/${userId}`);
        return response.data;
    } catch (err) {
        throw err.response ? err.response.data : err.message;
    }
};

//Employees
export const createEmployees = async (employeeData) => {
    try {
        const response = await api.post('/employees', employeeData);
        return response.data;
    } catch (err) {
        throw err.response ? err.response.data : err.message;
    }
};

export const getEmployees = async (page = 1) => {
    try {
        const response = await api.get('/employees', {
            params: {
                page, // Pass the current page number
                limit: 20, // Optionally, you can also pass the limit here
            },
        });
        return response.data;
    } catch (err) {
        throw err.response ? err.response.data : err.message;
    }
};

// Fetch multiple visitors by IDs
// export const getVisitorsByIds = async (employeeIds) => {
//     try {
//         const response = await api.get('/employees/bulk', {
//             params: { ids: employeeIds.join(',') }, // Pass visitor IDs as query params
//         });
//         return response.data;
//     } catch (err) {
//         throw err.response ? err.response.data : err.message;
//     }
// };

//for pagination
export const getEmployeesByIds = async (employeeIds, page = 1, limit = 20) => {
    try {
        const response = await api.get('/employees/bulk', {
            params: { ids: employeeIds.join(','), page, limit },
        });
        return response.data;
    } catch (err) {
        throw err.response ? err.response.data : err.message;
    }
};

export const getEmployeeById = async (employeeId) => {
    try {
        const response = await api.get(`/employees/${employeeId}`);
        return response.data;
    } catch (err) {
        throw err.response ? err.response.data : err.message;
    }
};

export const updateEmployee = async (employeeId, employeeData) => {
    try {
        const response = await api.put(`/employees/${employeeId}`, employeeData);
        return response.data;
    } catch (err) {
        throw err.response ? err.response.data : err.message;
    }
};

export const deleteEmployee = async (employeeId) => {
    try {
        const response = await api.delete(`/employees/${employeeId}`);
        return response.data;
    } catch (err) {
        throw err.response ? err.response.data : err.message;
    }
};


//Items
export const createItem = async (itemData) => {
    try {
        const response = await api.post('/items', itemData);
        return response.data;
    } catch (err) {
        throw err.response ? err.response.data : err.message;
    }
};

export const getItems = async (page = 1, limit = 20, sortField = 'createdAt', sortOrder = 'desc', lowStockSort) => {
    try {
        // const response = await api.get('/items');
        const response = await api.get('/items', {
            params: {
                page, // Pass the current page number
                limit, // Optionally, you can also pass the limit here
                sortField, // Field to sort by
                sortOrder, // Sort order (asc or desc)
                lowStockSort,
            },
        });
        return response.data;
    } catch (err) {
        throw err.response ? err.response.data : err.message;
    }
};

export const searchItem = async (page = 1, query, field) => {
    try {
        const response = await api.get('/items/search', {
            params: {
                page,
                limit:20,
                query, // Pass the current page number
                field, // Optionally, you can also pass the limit here
            },
        });
        return response.data;
    } catch (err) {
        throw err.response ? err.response.data : err.message;
    }
};


export const getItemByEmployeeId = async (employeeId) => {
    try {
        const response = await api.get(`/items/${employeeId}`);
        return response.data;
    } catch (err) {
        throw err.response ? err.response.data : err.message;
    }
};

export const getItemById = async (itemId) => {
    try {
        const response = await api.get(`/items/${itemId}`);
        return response.data;
    } catch (err) {
        throw err.response ? err.response.data : err.message;
    }
};

export const updateItem = async (itemId, itemData) => {
    try {
        const response = await api.put(`/items/${itemId}`, itemData);
        return response.data;
    } catch (err) {
        throw err.response ? err.response.data : err.message;
    }
};

export const updateItemQuantity = async (itemId, itemquantity) => {
    try {
        const response = await api.put(`/items/quantity/${itemId}`, itemquantity);
        return response.data;
    } catch (err) {
        throw err.response ? err.response.data : err.message;
    }
};

export const deleteItem = async (itemId) => {
    try {
        const response = await api.delete(`/items/${itemId}`);
        return response.data;
    } catch (err) {
        throw err.response ? err.response.data : err.message;
    }
};

//Groups
export const createGroup = async (groupData) => {
    try {
        const response = await api.post('/groups', groupData);
        return response.data;
    } catch (err) {
        throw err.response ? err.response.data : err.message;
    }
};

export const getGroups = async () => {
    try {
        // const response = await api.get('/items');
        const response = await api.get('/groups');
        return response.data;
    } catch (err) {
        throw err.response ? err.response.data : err.message;
    }
};

export const updateGroup = async (groupId, groupData) => {
    try {
        const response = await api.put(`/groups/${groupId}`, groupData);
        return response.data;
    } catch (err) {
        throw err.response ? err.response.data : err.message;
    }
};

export const deleteGroup = async (groupId) => {
    try {
        const response = await api.delete(`/groups/${groupId}`);
        return response.data;
    } catch (err) {
        throw err.response ? err.response.data : err.message;
    }
};

// src/services/rgpService.js

export const createRGP = async (rgpData) => {
    try {
        const response = await api.post('/rgp', rgpData);
        return response.data;
    } catch (err) {
        throw err.response ? err.response.data : err.message;
    }
};

export const getRGPs = async (filters = {}) => {
    try {
        const params = new URLSearchParams();
        
        if (filters.status) params.append('status', filters.status);
        if (filters.type) params.append('type', filters.type);
        if (filters.startDate) params.append('startDate', filters.startDate);
        if (filters.endDate) params.append('endDate', filters.endDate);
        
        const response = await api.get(`/rgp?${params.toString()}`);
        return response.data;
    } catch (err) {
        throw err.response ? err.response.data : err.message;
    }
};

export const getRGPById = async (rgpId) => {
    try {
        const response = await api.get(`/rgp/${rgpId}`);
        return response.data;
    } catch (err) {
        throw err.response ? err.response.data : err.message;
    }
};

export const getRGPByNumber = async (rgpNumber) => {
    try {
        const response = await api.get(`/rgp/number/${rgpNumber}`);
        return response.data;
    } catch (err) {
        throw err.response ? err.response.data : err.message;
    }
};

export const updateRGP = async (rgpId, rgpData) => {
    try {
        const response = await api.put(`/rgp/${rgpId}`, rgpData);
        return response.data;
    } catch (err) {
        throw err.response ? err.response.data : err.message;
    }
};

export const deleteRGP = async (rgpId) => {
    try {
        const response = await api.delete(`/rgp/${rgpId}`);
        return response.data;
    } catch (err) {
        throw err.response ? err.response.data : err.message;
    }
};

export const getRGPStatusCounts = async () => {
    try {
        const response = await api.get('/rgp/status-counts');
        return response.data;
    } catch (err) {
        throw err.response ? err.response.data : err.message;
    }
};

export const searchRGPs = async (searchTerm, field = 'all') => {
    try {
        const response = await api.get(`/rgp/search?term=${searchTerm}&field=${field}`);
        return response.data;
    } catch (err) {
        throw err.response ? err.response.data : err.message;
    }
};

export const fetchStats = async (field) => {
    try {
        const response = await api.get(`/issuestats/stats/${field}`);
        return response.data; // Example: [{ _id: 'open', count: 30 }, { _id: 'resolved', count: 20 }]
    } catch (err) {
        console.error('Error fetching field distribution:', err);
        return [];
    }
};

export const transformDataForChart = (distribution) => {
    return {
        labels: distribution.map((item) => item._id), // ['open', 'resolved']
        datasets: [
            {
                data: distribution.map((item) => item.count), // [30, 20]
                backgroundColor: ['#ea4335', '#4285f4'], // Colors for each status
            },
        ],
    };
};

export const fetchOfficeComplaintStats = async (startDate, endDate) => {
    try {
        const response = await api.get(`/issuestats/office-complaint-stats?startDate=${startDate}&endDate=${endDate}`);
        return response.data;
    } catch (err) {
        console.error('Error fetching office complaint stats:', err);
        return [];
    }
};

export default api;